window.Eurus = window.Eurus || {};
window.Eurus.loadedScript = window.Eurus.loadedScript || new Set();

if (!window.Eurus.loadedScript.has('description-disclosure.js')) {
  window.Eurus.loadedScript.add('description-disclosure.js');

  (() => {
    const ROOT_SELECTOR = '[data-description-disclosure]';
    const CONTENT_SELECTOR = '[data-description-content]';
    const WRAP_SELECTOR = '[data-description-wrap]';
    const TOGGLE_SELECTOR = '[data-description-toggle]';
    const EXPANSION_CONTAINER_SELECTOR = '[data-description-expansion-container]';
    const states = new WeakMap();
    let descriptionId = 0;
    let resizeFrame = null;

    const setExpansionContainer = (root, expanded) => {
      const container = root.closest(EXPANSION_CONTAINER_SELECTOR);
      if (!container) return;

      if (expanded) {
        container.style.setProperty('--description-expanded-min-height', `${container.getBoundingClientRect().height}px`);
        container.classList.add('description-disclosure-expanded');
      } else {
        container.classList.remove('description-disclosure-expanded');
        container.style.removeProperty('--description-expanded-min-height');
      }
    };

    const setExpanded = (root, expanded) => {
      const state = states.get(root);
      if (!state || !state.canTruncate) return;

      state.expanded = expanded;
      root.classList.toggle('is-expanded', expanded);
      state.wrap.classList.toggle('is-collapsed', !expanded);
      state.toggle.setAttribute('aria-expanded', String(expanded));
      state.more.hidden = expanded;
      state.less.hidden = !expanded;
      setExpansionContainer(root, expanded);
    };

    const measure = root => {
      const state = states.get(root);
      if (!state || !root.isConnected) return;

      const width = state.content.getBoundingClientRect().width;
      if (width <= 0) return;

      const clone = state.content.cloneNode(true);
      clone.removeAttribute('id');
      clone.removeAttribute('data-description-content');
      clone.setAttribute('aria-hidden', 'true');
      Object.assign(clone.style, {
        position: 'absolute',
        visibility: 'hidden',
        pointerEvents: 'none',
        zIndex: '-1',
        top: '0',
        left: '0',
        width: `${width}px`,
        height: 'auto',
        maxHeight: 'none',
        display: '-webkit-box',
        overflow: 'hidden',
        webkitBoxOrient: 'vertical',
        webkitLineClamp: String(state.lines),
        lineClamp: String(state.lines)
      });
      root.append(clone);

      const collapsedHeight = clone.getBoundingClientRect().height;
      clone.style.display = 'block';
      clone.style.overflow = 'visible';
      clone.style.webkitLineClamp = 'unset';
      clone.style.lineClamp = 'unset';
      const fullHeight = clone.getBoundingClientRect().height;
      clone.remove();

      state.canTruncate = fullHeight > collapsedHeight + 1;
      state.wrap.classList.toggle('can-truncate', state.canTruncate);
      state.toggle.hidden = !state.canTruncate;

      if (!state.canTruncate) {
        state.expanded = false;
        root.classList.remove('is-expanded');
        state.wrap.classList.remove('is-collapsed');
        state.toggle.setAttribute('aria-expanded', 'false');
        state.more.hidden = false;
        state.less.hidden = true;
        setExpansionContainer(root, false);
        return;
      }

      state.wrap.classList.toggle('is-collapsed', !state.expanded);
    };

    const queueMeasure = root => {
      const state = states.get(root);
      if (!state) return;
      if (state.measureFrame) cancelAnimationFrame(state.measureFrame);
      state.measureFrame = requestAnimationFrame(() => {
        state.measureFrame = null;
        measure(root);
      });
    };

    const initialize = root => {
      if (states.has(root)) return;

      const content = root.querySelector(CONTENT_SELECTOR);
      const wrap = root.querySelector(WRAP_SELECTOR);
      const toggle = root.querySelector(TOGGLE_SELECTOR);
      const more = toggle?.querySelector('[data-description-more]');
      const less = toggle?.querySelector('[data-description-less]');
      if (!content || !wrap || !toggle || !more || !less) return;

      const lines = Math.max(1, Number.parseInt(root.dataset.descriptionLines, 10) || 2);
      const id = `description-disclosure-${++descriptionId}`;
      content.id = id;
      toggle.setAttribute('aria-controls', id);

      const state = {
        content,
        wrap,
        toggle,
        more,
        less,
        lines,
        expanded: false,
        canTruncate: false,
        measureFrame: null,
        resizeObserver: null
      };
      states.set(root, state);
      wrap.classList.add('is-collapsed');

      if ('ResizeObserver' in window) {
        state.resizeObserver = new ResizeObserver(() => queueMeasure(root));
        state.resizeObserver.observe(root);
      }

      queueMeasure(root);
    };

    const initializeWithin = scope => {
      if (!scope) return;
      if (scope.matches?.(ROOT_SELECTOR)) initialize(scope);
      scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initialize);
    };

    const cleanupWithin = scope => {
      if (!scope) return;
      const roots = [];
      if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
      scope.querySelectorAll?.(ROOT_SELECTOR).forEach(root => roots.push(root));
      roots.forEach(root => {
        const state = states.get(root);
        state?.resizeObserver?.disconnect();
        if (state?.measureFrame) cancelAnimationFrame(state.measureFrame);
        setExpansionContainer(root, false);
        states.delete(root);
      });
    };

    document.addEventListener('click', event => {
      const toggle = event.target.closest?.(TOGGLE_SELECTOR);
      if (!toggle) return;
      const root = toggle.closest(ROOT_SELECTOR);
      const state = states.get(root);
      if (!state?.canTruncate) return;

      const expanded = !state.expanded;
      setExpanded(root, expanded);
      if (!expanded) toggle.scrollIntoView({ block: 'nearest' });
    });

    document.addEventListener('shopify:section:load', event => initializeWithin(event.target));
    document.addEventListener('shopify:section:unload', event => cleanupWithin(event.target));

    const mutationObserver = new MutationObserver(records => {
      records.forEach(record => record.addedNodes.forEach(node => initializeWithin(node)));
    });
    mutationObserver.observe(document.documentElement, { childList: true, subtree: true });

    window.addEventListener('resize', () => {
      if (resizeFrame) cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = null;
        document.querySelectorAll(ROOT_SELECTOR).forEach(queueMeasure);
      });
    });

    document.fonts?.ready?.then(() => {
      document.querySelectorAll(ROOT_SELECTOR).forEach(queueMeasure);
    });

    initializeWithin(document);
  })();
}
